/**
 * FrontPanel — the gameplay-view WYSIWYG area.
 *
 * Renders a category bar (4 teal rectangles with cream backing, matching the
 * game's ExplanationBanner) above 4 columns × 6 rows of editable clue cells.
 *
 * "Player View" toggle replaces hidden-cell text with actual HintButton/GuessButton
 * SVGs so the puzzle creator can see the board as a player would at game start.
 *
 * Each column has a drag handle at the top for reordering via drag-and-drop.
 */

import React, { useState, useCallback, useRef, useMemo } from 'react';
import type { PuzzleJSON } from '../../types';
import type { EditorActions } from './useEditorState';
import EditorClueCell from './EditorClueCell';
import EditorBanner from './EditorBanner';
import HiddenToggle from './HiddenToggle';
import { fitRecapCategoryText } from '../../utils/textFitting';
import styles from './FrontPanel.module.css';

interface FrontPanelProps {
  puzzle: PuzzleJSON;
  actions: EditorActions;
  onCellFocus?: (col: number, row: number) => void;
}

/** Key format: "col-pair" */
type AnimState = Record<string, 'slideUp' | 'slideDown'>;

// ─── Category bar constants (matching ExplanationBanner layout) ──
const RECT_W = 208;
const RECT_H = 132;
const TEAL_BG = '#1f7d7e';
const CREAM = '#f7e5c5';

const FrontPanel: React.FC<FrontPanelProps> = ({ puzzle, actions, onCellFocus }) => {
  const [anims, setAnims] = useState<AnimState>({});
  const [playerView, setPlayerView] = useState(false);

  // Drag-and-drop state
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const dragCounter = useRef<Record<number, number>>({});

  const handleToggle = useCallback((colIdx: number, pairIdx: number) => {
    const pair = puzzle.columns[colIdx].pairs[pairIdx];
    const direction = pair.hiddenIndex === 0 ? 'slideDown' : 'slideUp';

    const ok = actions.toggleHidden(colIdx, pairIdx);
    if (!ok) return;

    const key = `${colIdx}-${pairIdx}`;
    setAnims(prev => ({ ...prev, [key]: direction }));
    setTimeout(() => {
      setAnims(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, 350);
  }, [puzzle, actions]);

  const togglePlayerView = useCallback(() => setPlayerView(prev => !prev), []);

  // ── Drag-and-drop handlers ──
  const handleDragStart = useCallback((e: React.DragEvent, colIdx: number) => {
    setDragFrom(colIdx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(colIdx));
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent, colIdx: number) => {
    e.preventDefault();
    dragCounter.current[colIdx] = (dragCounter.current[colIdx] ?? 0) + 1;
    setDragOver(colIdx);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDragLeave = useCallback((_e: React.DragEvent, colIdx: number) => {
    dragCounter.current[colIdx] = (dragCounter.current[colIdx] ?? 1) - 1;
    if (dragCounter.current[colIdx] <= 0) {
      dragCounter.current[colIdx] = 0;
      setDragOver(prev => prev === colIdx ? null : prev);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, toIdx: number) => {
    e.preventDefault();
    const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (!isNaN(fromIdx) && fromIdx !== toIdx) {
      actions.reorderColumns(fromIdx, toIdx);
    }
    setDragFrom(null);
    setDragOver(null);
    dragCounter.current = {};
  }, [actions]);

  const handleDragEnd = useCallback(() => {
    setDragFrom(null);
    setDragOver(null);
    dragCounter.current = {};
  }, []);

  return (
    <div className={styles.frontPanel}>
      <div className={styles.bannerRow}>
        <EditorBanner
          solution={puzzle.solution.primary}
          onChange={actions.setSolution}
        />
        <button
          className={`${styles.maskBtn} ${playerView ? styles.maskBtnActive : ''}`}
          onClick={togglePlayerView}
          title={playerView ? 'Masks on — click to show editor view' : 'Masks off — click to show player view'}
        >
          <MaskIcon active={playerView} />
        </button>
      </div>

      {/* Category bar — 4 teal rectangles with cream backing (matching game) */}
      <div className={styles.categoryBar}>
        {puzzle.columns.map((col, colIdx) => (
          <CategoryRect
            key={colIdx}
            text={col.category}
            onTextChange={text => actions.setCategory(colIdx, text)}
          />
        ))}
      </div>

      <div className={styles.columnsRow}>
        {puzzle.columns.map((col, colIdx) => {
          const isDragging = dragFrom === colIdx;
          const isOver = dragOver === colIdx && dragFrom !== colIdx;
          return (
            <div
              key={colIdx}
              className={styles.column}
              style={{
                opacity: isDragging ? 0.4 : 1,
                transition: 'opacity 150ms ease',
              }}
              onDragEnter={e => handleDragEnter(e, colIdx)}
              onDragOver={handleDragOver}
              onDragLeave={e => handleDragLeave(e, colIdx)}
              onDrop={e => handleDrop(e, colIdx)}
            >
              {/* Drag handle */}
              <div
                className={`${styles.dragHandle} ${isOver ? styles.dragHandleOver : ''}`}
                draggable
                onDragStart={e => handleDragStart(e, colIdx)}
                onDragEnd={handleDragEnd}
                title="Drag to reorder column"
              >
                <DragHandleIcon />
              </div>

              {col.pairs.map((pair, pairIdx) => {
                const animKey = `${colIdx}-${pairIdx}`;
                const anim = anims[animKey] ?? null;
                return (
                  <React.Fragment key={pairIdx}>
                    {pairIdx > 0 && <div className={styles.pairGap} />}
                    <div className={styles.pairGroup}>
                      {/* Toggle button — LEFT side */}
                      <HiddenToggle
                        hiddenIndex={pair.hiddenIndex}
                        pairIndex={pairIdx}
                        onClick={() => handleToggle(colIdx, pairIdx)}
                      />
                      {/* Top cell */}
                      <EditorClueCell
                        text={pair.clues[0]}
                        rowIndex={pairIdx * 2}
                        pairIndex={pairIdx}
                        isHidden={pair.hiddenIndex === 0}
                        displayHint={puzzle.displayHints?.clues?.[`${colIdx}-${pairIdx * 2}`]}
                        onChange={text => actions.setClueText(colIdx, pairIdx, 0, text)}
                        onFocus={() => onCellFocus?.(colIdx, pairIdx * 2)}
                        slideAnim={pair.hiddenIndex === 0 ? anim : null}
                        showButton={playerView && pair.hiddenIndex === 0}
                      />
                      {/* Bottom cell */}
                      <EditorClueCell
                        text={pair.clues[1]}
                        rowIndex={pairIdx * 2 + 1}
                        pairIndex={pairIdx}
                        isHidden={pair.hiddenIndex === 1}
                        displayHint={puzzle.displayHints?.clues?.[`${colIdx}-${pairIdx * 2 + 1}`]}
                        onChange={text => actions.setClueText(colIdx, pairIdx, 1, text)}
                        onFocus={() => onCellFocus?.(colIdx, pairIdx * 2 + 1)}
                        slideAnim={pair.hiddenIndex === 1 ? anim : null}
                        showButton={playerView && pair.hiddenIndex === 1}
                      />
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── CategoryRect — editable teal rect with cream backing ──
const CategoryRect: React.FC<{ text: string; onTextChange: (text: string) => void }> = ({ text, onTextChange }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [editing, setEditing] = useState(false);
  const fit = useMemo(() => fitRecapCategoryText(text || 'Category', RECT_W), [text]);
  const isEmpty = !text;

  const handleClick = useCallback(() => {
    setEditing(true);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  const handleBlur = useCallback(() => setEditing(false), []);

  // SVG preview dimensions (inner content area after padding)
  const SVG_W = RECT_W - 20;
  const SVG_H = RECT_H - 16;
  const lineHeight = fit.fontSize * 1.3;
  const totalH = fit.lines.length * lineHeight;
  const startY = (SVG_H - totalH) / 2 + lineHeight / 2;

  return (
    <div className={styles.categoryRectWrap}>
      {/* Solid teal rect — no cream backing, sharp corners */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: TEAL_BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '8px 10px',
          boxSizing: 'border-box',
          cursor: 'text',
        }}
        onClick={!editing ? handleClick : undefined}
      >
        {/* SVG preview */}
        {!editing && (
          isEmpty ? (
            <span style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 16,
              fontWeight: 600,
              fontStyle: 'italic',
              color: CREAM,
              opacity: 0.2,
            }}>
              Category
            </span>
          ) : (
            <svg
              width={SVG_W}
              height={SVG_H}
              viewBox={`0 0 ${SVG_W} ${SVG_H}`}
              style={{ pointerEvents: 'none' }}
            >
              {fit.lines.length === 1 ? (
                <text
                  x="50%"
                  y="50%"
                  dy=".1em"
                  fill={CREAM}
                  fontFamily="Inter, sans-serif"
                  fontSize={fit.fontSize}
                  fontWeight="600"
                  fontStyle="italic"
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  {fit.lines[0]}
                </text>
              ) : (
                <text
                  fill={CREAM}
                  fontFamily="Inter, sans-serif"
                  fontSize={fit.fontSize}
                  fontWeight="600"
                  fontStyle="italic"
                  textAnchor="middle"
                >
                  {fit.lines.map((line, i) => (
                    <tspan
                      key={i}
                      x="50%"
                      y={startY + i * lineHeight}
                      dominantBaseline="middle"
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
              )}
            </svg>
          )
        )}

        {/* Textarea edit overlay */}
        {editing && (
          <textarea
            ref={textareaRef}
            value={text}
            onChange={e => onTextChange(e.target.value)}
            onBlur={handleBlur}
            placeholder="Category"
            maxLength={120}
            rows={3}
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              textAlign: 'center',
              fontFamily: 'Inter, sans-serif',
              fontSize: fit.fontSize,
              fontWeight: 600,
              fontStyle: 'italic',
              color: CREAM,
              width: '100%',
              height: '100%',
              padding: 0,
              resize: 'none',
              overflow: 'hidden',
              lineHeight: 1.3,
              boxSizing: 'border-box',
            }}
          />
        )}
      </div>
    </div>
  );
};

/** Drag handle icon — three horizontal bars */
const DragHandleIcon: React.FC = () => (
  <svg width={28} height={10} viewBox="0 0 28 10">
    <rect x={4} y={1} width={20} height={2} rx={1} fill="#f7e5c5" opacity={0.4} />
    <rect x={4} y={4.5} width={20} height={2} rx={1} fill="#f7e5c5" opacity={0.4} />
    <rect x={4} y={8} width={20} height={2} rx={1} fill="#f7e5c5" opacity={0.4} />
  </svg>
);

/** Bandit-style mask icon — horizontal band with eye holes */
const MaskIcon: React.FC<{ active: boolean }> = ({ active }) => {
  const fill = active ? '#f7e5c5' : '#6a6460';
  const stroke = active ? '#f7e5c5' : '#a09890';
  const eyeFill = active ? '#d26f28' : '#3a3630';
  return (
    <svg width={32} height={18} viewBox="0 0 40 20">
      {/* Horizontal band — pointed ends, slight curve on top/bottom */}
      <path
        d="M 1 10 L 4 3 Q 10 1, 20 4 Q 30 1, 36 3 L 39 10 L 36 17 Q 30 19, 20 16 Q 10 19, 4 17 Z"
        fill={fill}
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
      {/* Left eye hole */}
      <ellipse cx={12} cy={10} rx={5.5} ry={4} fill={eyeFill} stroke={stroke} strokeWidth={1} />
      {/* Right eye hole */}
      <ellipse cx={28} cy={10} rx={5.5} ry={4} fill={eyeFill} stroke={stroke} strokeWidth={1} />
    </svg>
  );
};

export default React.memo(FrontPanel);
